import Ajv from 'ajv';
import schema from '../data/combat-rules.schema.json';

export interface CombatRule {
  id: string; label: string; kind: string; subject: string; mode: string;
  params: Record<string, any>; references: { table: string; key: string }[];
  legacy_actions: { trigger_code: string; action_order: number; action_type: string; action_params: Record<string, unknown> }[];
}
export interface CombatRulePack {
  schema_version: 1; owner: string; source: string; rules: CombatRule[];
  unresolved: { id: string; detail: string; required_fields: string[]; known_values: Record<string, unknown> }[];
}
const validateSchema = new Ajv({ allErrors: true, strict: false }).compile(schema);
const expectedModes: Record<string, string> = {
  encounter_scope: 'augment', idle_punish: 'augment', adjacency_stacks: 'replace_skill_resolution',
  split_aoe: 'replace_skill_resolution', stack_modifiers: 'replace_effect', damage_filter: 'replace_effect',
  periodic_damage: 'replace_effect', actor_policy: 'override_actor_policy', party_heal: 'replace_skill_resolution',
  encounter_victory: 'replace_skill_resolution', hybrid_debuff: 'replace_effect', healing_ground: 'override_actor_policy',
  phase_actor_policy: 'override_actor_policy', stack_hit: 'replace_skill_resolution', convert_stacks: 'augment',
  split_fallback_aoe: 'replace_skill_resolution', carrier_aura: 'replace_effect', create_ground: 'replace_skill_resolution',
  expand_ground: 'replace_skill_resolution', ground_damage: 'replace_effect', elapsed_stat_ramp: 'replace_trigger_skill_dispatch',
  frenzy_variants: 'replace_effect',
  hp_loss_cooldown: 'override_actor_policy', assault_sequence: 'replace_skill_resolution',
};
export function validateCombatRulePack(value: unknown): string[] {
  if (!validateSchema(value)) return (validateSchema.errors || []).map(e => `${e.instancePath || '/'} ${e.message}`);
  const pack = value as CombatRulePack;
  const errors: string[] = [], ids = new Set<string>(), bindings = new Set<string>();
  for (const r of pack.rules) {
    if (ids.has(r.id)) errors.push(`중복 규칙: ${r.id}`);
    ids.add(r.id);
    const binding = `${r.subject}/${r.kind}`;
    if (bindings.has(binding)) errors.push(`중복 실행 바인딩: ${binding}`);
    bindings.add(binding);
    if (r.mode !== expectedModes[r.kind]) errors.push(`${r.id}: 실행 방식이 규칙 종류와 다릅니다.`);
    const p = r.params;
    // Reject unsafe numbers even if their JSON type is correct.
    const inspect = (node: any, path: string) => {
      if (node && typeof node === 'object') Object.entries(node).forEach(([k, v]) => inspect(v, `${path}/${k}`));
      else if (typeof node === 'number' && !Number.isFinite(node)) errors.push(`${path}: 유한한 수가 필요합니다.`);
    };
    inspect(p, r.id);
    ['period_s','first_tick_s','duration_s','max_stacks','steps','lifetime_s','max_heal_pulses'].forEach(key => {
      if (p[key] != null && p[key] <= 0) errors.push(`${r.id}/${key}: 0보다 커야 합니다.`);
    });
    ['hex_edge_radius','expand_hex_edges','power_percent','damage_power_percent'].forEach(key => {
      if (p[key] != null && p[key] < 0) errors.push(`${r.id}/${key}: 음수가 될 수 없습니다.`);
    });
    if (r.kind === 'carrier_aura' && (p.tick_offsets_s.some((n: number, i: number, a: number[]) => n <= 0 || n > p.duration_s || (i > 0 && n <= a[i - 1])))) errors.push(`${r.id}: 틱은 지속시간 내 오름차순이어야 합니다.`);
    if (r.kind === 'elapsed_stat_ramp' && Math.abs(1 + p.steps * p.percent_per_step / 100 - p.minimum_multiplier) > 1e-9) errors.push(`${r.id}: 감소량과 최종 배율이 일치하지 않습니다.`);
    if (r.kind === 'hp_loss_cooldown' && (p.minimum_cooldown_s <= 0 || p.minimum_cooldown_s > p.base_cooldown_s || p.hp_loss_step_percent <= 0 || p.reduction_s_per_step <= 0)) errors.push(`${r.id}: 잘못된 쿨타임 하한 또는 감소 단위입니다.`);
    if (r.kind === 'assault_sequence' && (p.hits[0].target !== 'highest_hp_ratio_player' || p.hits[1].target !== 'top_aggro' || p.hits[0].delay_s !== 0 || p.hits[1].delay_s !== 1)) errors.push(`${r.id}: 정령 습격의 대상 순서 또는 간격이 다릅니다.`);
    if (r.kind === 'create_ground' && p.tile_count !== 1 + 3 * p.hex_edge_radius * (p.hex_edge_radius + 1)) errors.push(`${r.id}: 육각 반경과 타일 수가 일치하지 않습니다.`);
    if (r.kind === 'frenzy_variants') {
      const variants = new Set<string>();
      for (const v of p.variants) {
        if (variants.has(v.skill)) errors.push(`${r.id}: 광분 스킬 중복 ${v.skill}`);
        variants.add(v.skill);
        if (v.max_stacks < 1 || v.attack_percent < 0 || v.attack_speed_percent < 0 || v.duration_s === 0 || v.duration_s < -1) errors.push(`${v.skill}: 잘못된 광분 수치`);
      }
    }
  }
  return errors;
}

type Rows = Record<string, unknown>[];
export type CombatTables = Record<string, Rows>;
export function buildCombatRuleBundle(tables: CombatTables) {
  const packs: CombatRulePack[] = [], errors: string[] = [];
  const read = (value: unknown, owner: string) => {
    if (value == null) return;
    const issues = validateCombatRulePack(value);
    if (issues.length) { errors.push(...issues.map(e => `${owner}: ${e}`)); return; }
    const pack = value as CombatRulePack;
    if (pack.owner !== owner) { errors.push(`${owner}: 규칙 소유자가 다릅니다.`); return; }
    packs.push(pack);
  };
  for (const row of tables.raid_entity_renewed || []) read(row.combat_rules, String(row.entity_code));
  for (const row of tables.boss_effect_renewed || []) read((row.special_params as any)?.combat_rules, String(row.key));
  const owners = new Set<string>();
  for (const p of packs) {
    if (owners.has(p.owner)) errors.push(`${p.owner}: 중복 규칙 묶음`);
    owners.add(p.owner);
    const canonical = (v: any): string => JSON.stringify(v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.keys(v).sort().map(k => [k, JSON.parse(canonical(v[k]))])) : Array.isArray(v) ? v.map(x => JSON.parse(canonical(x))) : v);
    for (const r of p.rules) for (const binding of r.legacy_actions) {
      const trigger = (tables.trigger_definition_v3 || []).find(t => t.trigger_code === binding.trigger_code);
      const action = (tables.trigger_action_v3 || []).find(a => a.trigger_id === trigger?.id && a.action_order === binding.action_order);
      if (!trigger || trigger.is_enabled === false || !action || action.action_type !== binding.action_type || canonical(action.action_params) !== canonical(binding.action_params)) errors.push(`${p.owner}/${r.id}: 기존 트리거 행동이 달라졌습니다: ${binding.trigger_code}/${binding.action_order}`);
    }
    for (const r of p.rules) for (const ref of r.references) {
      const key = ref.table === 'raid_entity_renewed' ? 'entity_code' : 'key';
      if (!(tables[ref.table] || []).some(row => row[key] === ref.key)) errors.push(`${p.owner}/${r.id}: 없는 참조 ${ref.table}/${ref.key}`);
    }
    for (const r of p.rules) {
      const inspectRefs = (value: unknown) => {
        if (value && typeof value === 'object') Object.values(value).forEach(inspectRefs);
        else if (typeof value === 'string' && (value.includes('|') || /^(BOSS_|OBJECT_)/.test(value))) {
          if (!r.references.some(ref => ref.key === value)) errors.push(`${p.owner}/${r.id}: 매개변수의 참조가 누락됐습니다: ${value}`);
        }
      };
      inspectRefs(r.params);
    }
    for (const r of p.rules.filter(r => r.kind === 'frenzy_variants')) {
      const relations = (tables.boss_skill_effect_relation_renewed || []).filter(row => row.effect === p.owner);
      for (const rel of relations) {
        const v = r.params.variants.find((v: any) => v.skill === rel.skill && v.relation_order === rel.order);
        if (!v) errors.push(`${String(rel.skill)}: 광분 적용 규칙이 없습니다.`);
        else if ((v.skill !== 'DREARMENT|EMPOWER' && v.attack_percent !== rel.power) || v.duration_s !== rel.duration_s) errors.push(`${v.skill}: 광분 규칙과 연결 위력/지속시간이 달라졌습니다.`);
      }
      for (const v of r.params.variants) if (!relations.some(rel => rel.skill === v.skill && rel.order === v.relation_order)) errors.push(`${v.skill}: 광분 연결이 없어졌습니다.`);
    }
  }
  return { schema_version: 1, integration_status: 'requires_engine_adapter',
    executable: false, // Exporting data is not proof that the game implements these handlers.
    coverage: 'reviewed_rule_packs_only', packs, validation: { valid: errors.length === 0, errors },
    unresolved: packs.flatMap(p => p.unresolved.map(u => ({ owner: p.owner, ...u }))),
  };
}

// Reference calculations: the engine adapter must use the same units and ordering.
export function stackMultiplier(percent: number, stacks: number, mode: string) {
  if (!Number.isFinite(percent) || !Number.isInteger(stacks) || stacks < 0) throw Error('잘못된 중첩 값');
  if (mode === 'additive_percent') return Math.max(0, 1 + percent * stacks / 100);
  if (mode === 'multiplicative_percent') return Math.pow(Math.max(0, 1 + percent / 100), stacks);
  throw Error('지원하지 않는 중첩 계산');
}
export function rampMultiplier(elapsedS: number, params: Record<string, any>) {
  const steps = Math.min(params.steps, Math.max(0, Math.floor((elapsedS - params.start_after_s) / params.period_s) + 1));
  return Math.max(params.minimum_multiplier, 1 + steps * params.percent_per_step / 100);
}
export function splitDamage(basePower: number, powerPercent: number, hitCount: number) {
  if (!Number.isInteger(hitCount) || hitCount < 0) throw Error('잘못된 피격 인원');
  return hitCount === 0 ? 0 : basePower * powerPercent / 100 / hitCount;
}
export type Hex = [number, number];
// The caller resets baselineHpPercent to post-heal HP after every effective heal.
export function hpLossCooldown(baselineHpPercent: number, currentHpPercent: number, params: Record<string, any>) {
  if (![baselineHpPercent, currentHpPercent].every(n => Number.isFinite(n) && n >= 0 && n <= 100)) throw Error('잘못된 체력 비율');
  const steps = Math.floor(Math.max(0, baselineHpPercent - currentHpPercent) / params.hp_loss_step_percent);
  return Math.max(params.minimum_cooldown_s, params.base_cooldown_s - steps * params.reduction_s_per_step);
}
export function hexDisk(center: Hex, radius: number): Hex[] {
  if (!Number.isInteger(radius) || radius < 0) throw Error('잘못된 육각 반경');
  const cells: Hex[] = [];
  for (let q = -radius; q <= radius; q++) for (let r = Math.max(-radius, -q - radius); r <= Math.min(radius, -q + radius); r++) cells.push([center[0] + q, center[1] + r]);
  return cells;
}
export function expandHexUnion(cells: Hex[], radius: number): Hex[] {
  return Array.from(new Map(cells.flatMap(cell => hexDisk(cell, radius)).map(cell => [cell.join(','), cell])).values());
}
